import { computed, ref, watch, type ComputedRef, type Ref } from 'vue';
import { readPref, resolveTheme, writePref, THEME_ATTR, type Theme, type ThemePref } from '@/lib/theme';

/**
 * One theme for the whole app, so the nav toggle and any other reader agree.
 *
 * The state is module-level rather than per-component: a second `useTheme()` must
 * observe the same choice, not start a second one.
 */

const DARK_QUERY = '(prefers-color-scheme: dark)';

/** Absent under Capacitor's older WebViews and in a non-DOM test env. */
const media: MediaQueryList | null =
  typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia(DARK_QUERY)
    : null;

const pref = ref<ThemePref>(readPref());
const systemPrefersDark = ref(media?.matches ?? false);

// Only fires while `pref` is `system`, but it is cheap and keeps `theme` honest either way.
media?.addEventListener?.('change', (event) => {
  systemPrefersDark.value = event.matches;
});

const theme = computed<Theme>(() => resolveTheme(pref.value, systemPrefersDark.value));

/**
 * The one place the attribute is written. `index.html` stamps the same value before
 * first paint; this keeps it correct afterwards, and is the whole mechanism on a
 * browser where the boot script was blocked.
 */
watch(
  theme,
  (value) => {
    if (typeof document !== 'undefined') document.documentElement.setAttribute(THEME_ATTR, value);
  },
  { immediate: true },
);

export interface UseTheme {
  /** What is painted right now. */
  theme: ComputedRef<Theme>;
  /** What the user asked for, which may be `system`. */
  pref: Ref<ThemePref>;
  setPref: (next: ThemePref) => void;
  /** Flips to the opposite of what is on screen, which is always an explicit choice. */
  toggle: () => void;
}

export function useTheme(): UseTheme {
  const setPref = (next: ThemePref): void => {
    pref.value = next;
    writePref(next);
  };

  return {
    theme,
    pref,
    setPref,
    toggle: () => setPref(theme.value === 'dark' ? 'light' : 'dark'),
  };
}
